import React, { useState, useRef } from 'react';
import { Megaphone, Search, Plus, Trash2, Sparkles, Loader2, Camera, Upload, Wrench } from 'lucide-react';
import { useData } from '../context/DataContext';
import { useT } from '../context/LanguageContext';
import { toast } from 'sonner';
import VerkoopAdvertentie from '../components/VerkoopAdvertentie';
import { VOERTUIG_TYPES } from '../components/AutoEditor';
import ScanAutopapieren from '../components/ScanAutopapieren';

const MAX_FOTOS = 6;
// Foto client-side verkleinen → kleine payload voor AI
const verkleinFoto = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = (e) => {
    const img = new Image();
    img.onload = () => {
      const max = 1280; let { width, height } = img;
      if (width > max || height > max) { const r = Math.min(max / width, max / height); width = Math.round(width * r); height = Math.round(height * r); }
      const canvas = document.createElement('canvas'); canvas.width = width; canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', 0.72));
    };
    img.onerror = reject; img.src = e.target.result;
  };
  reader.onerror = reject; reader.readAsDataURL(file);
});

export default function Advertentiemaker() {
  const { rdwLookup } = useData();
  const { t } = useT();
  const [loadingRdw, setLoadingRdw] = useState(false);
  const [modalAuto, setModalAuto] = useState(null);
  const [d, setD] = useState({
    kenteken: '', voertuigType: 'Personenauto', merk: '', model: '', bouwjaar: '', kmStand: '',
    kleur: '', brandstof: '', inkoopPrijs: '', verkoopPrijs: '', notitie: '',
  });
  const [werk, setWerk] = useState([{ omschrijving: '', kosten: '' }]); // wat is er aan de auto gedaan
  // Fase B — extra verkoopinfo (pluspunten + schade)
  const [extra, setExtra] = useState({
    distributie: false, apk: false, banden: false, remmen: false, sleutels: '',
    onderhoudshistorie: '', schadepunten: '',
  });
  const updExtra = (k, v) => setExtra(p => ({ ...p, [k]: v }));
  // Fase C — foto's vooraf
  const [fotos, setFotos] = useState([]); // [dataUrl]
const fotoRef = useRef(null);

  const upd = (k, v) => setD(prev => ({ ...prev, [k]: v }));

  const applyScan = (r) => setD(prev => ({
    ...prev,
    kenteken: prev.kenteken || (r.kenteken || ''),
    voertuigType: r.voertuigType && VOERTUIG_TYPES.some(v => v.v === r.voertuigType) ? r.voertuigType : prev.voertuigType,
    merk: r.merk || prev.merk,
    model: r.model || prev.model,
    bouwjaar: r.bouwjaar || prev.bouwjaar,
    kleur: r.kleur || prev.kleur,
    brandstof: r.brandstof || prev.brandstof,
  }));

  const handleRdw = async () => {
    if (!d.kenteken.trim()) { toast.error(t('Vul eerst het kenteken in', 'Vul eerst het kenteken in')); return; }
    setLoadingRdw(true);
    try {
      const r = await rdwLookup(d.kenteken);
      if (r && r.merk) {
        const bouwjaar = r.datumEersteToelating ? parseInt(String(r.datumEersteToelating).slice(0, 4)) : d.bouwjaar;
        const vs = (r.voertuigsoort || '').toLowerCase();
        let vtype = d.voertuigType;
        if (vs.includes('bromfiets') || vs.includes('snorfiets')) vtype = 'Scooter';
        else if (vs.includes('motor')) vtype = 'Motor';
        else if (vs.includes('aanhang') || vs.includes('oplegger')) vtype = 'Aanhanger';
        else if (vs.includes('bedrijfs') || vs.includes('bestel')) vtype = 'Bestelbus';
        else if (vs.includes('vracht')) vtype = 'Vrachtwagen';
        else if (vs.includes('personen')) vtype = 'Personenauto';
        setD(prev => ({ ...prev, voertuigType: vtype, merk: r.merk || prev.merk, model: r.handelsbenaming || prev.model, bouwjaar: bouwjaar || prev.bouwjaar, kleur: r.kleur || prev.kleur }));
        toast.success(
  `${t('RDW gevonden', 'RDW gevonden')}: ${r.merk} ${r.handelsbenaming || ''}`.trim()
);
      } else { toast.error(t('RDW lookup mislukt', 'RDW lookup mislukt')); }
    } catch (e) {
      toast.error(t('RDW lookup mislukt', 'RDW lookup mislukt'), { description: e.response?.data?.detail || e.message });
    } finally { setLoadingRdw(false); }
  };

  const setWerkRow = (i, k, v) => setWerk(rows => rows.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const addWerk = () => setWerk(rows => [...rows, { omschrijving: '', kosten: '' }]);
  const delWerk = (i) => setWerk(rows => rows.filter((_, idx) => idx !== i));

  const onFotos = async (e) => {
    const files = Array.from(e.target.files || []).filter(f => /image\//.test(f.type));
    if (fotoRef.current) fotoRef.current.value = '';
    if (!files.length) return;
    const ruimte = MAX_FOTOS - fotos.length;
    const verkleind = await Promise.all(files.slice(0, ruimte).map(verkleinFoto));
    setFotos(prev => [...prev, ...verkleind].slice(0, MAX_FOTOS));
  };
  const delFoto = (i) => setFotos(prev => prev.filter((_, idx) => idx !== i));

  const bouwPluspunten = () => {
    const p = [];
    if (extra.distributie) p.push(t('Distributieriem recent vervangen', 'Distributieriem recent vervangen'));
    if (extra.apk) p.push(t('Nieuwe APK', 'Nieuwe APK'));
    if (extra.banden) p.push(t('Nieuwe banden', 'Nieuwe banden'));
    if (extra.remmen) p.push(t('Nieuwe remmen', 'Nieuwe remmen'));
    if (String(extra.sleutels).trim()) p.push(`${extra.sleutels} ${t('sleutels', 'sleutels')}`);
    if (extra.onderhoudshistorie.trim()) p.push(`${t('Onderhoudshistorie', 'Onderhoudshistorie')}: ${extra.onderhoudshistorie.trim()}`);
    return p;
  };

  const maakAdvertentie = () => {
    if (!d.merk && !d.model && !d.kenteken) { toast.error(t('Vul minimaal kenteken of merk/model in', 'Vul minimaal kenteken of merk/model in')); return; }
    const kostenposten = werk
      .filter(w => (w.omschrijving || '').trim())
      .map((w, i) => ({ id: `w${i}`, omschrijving: w.omschrijving.trim(), kosten: Number(w.kosten) || 0 }));
    setModalAuto({
      kenteken: d.kenteken.trim().toUpperCase(), voertuigType: d.voertuigType,
      merk: d.merk.trim(), model: d.model.trim(), bouwjaar: d.bouwjaar,
      kmStand: Number(d.kmStand) || '', kleur: d.kleur, brandstof: d.brandstof,
      inkoopPrijs: Number(d.inkoopPrijs) || 0, verkoopPrijs: Number(d.verkoopPrijs) || 0,
      notitie: d.notitie, kostenposten,
      pluspunten: bouwPluspunten(),
      schadepunten: extra.schadepunten.trim(),
      onderhoudshistorie: extra.onderhoudshistorie.trim(),
      fotos,
    });
  };

  return (
    <div className="space-y-4" data-testid="advertentiemaker-page">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
        <div style={{ width: 44, height: 44, borderRadius: 11, background: 'linear-gradient(135deg,#7c3aed,#a855f7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Megaphone size={22} color="#fff" />
        </div>
        <div>
          <h2 style={{ margin: 0, fontSize: 22 }}>{t('Advertentie maken', 'Advertentie maken')}</h2>
          <p style={{ margin: 0, fontSize: 13.5, color: 'var(--cs-text-muted)' }}>
            {t('Kenteken invullen → AI maakt de advertentie, waardebepaling en foto-analyse.', 'Kenteken invullen → AI maakt de advertentie, waardebepaling en foto-analyse.')}
          </p>
        </div>
      </div>

      <div className="cs-card padded" style={{ maxWidth: 760 }}>
        {/* Kenteken + RDW */}
        <label className="cs-section-title">{t('Kenteken', 'Kenteken')}</label>
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <input className="cs-input" data-testid="adm-kenteken" placeholder="bv. 66-JXG-4" value={d.kenteken}
            onChange={e => upd('kenteken', e.target.value)} onKeyDown={e => e.key === 'Enter' && handleRdw()}
            style={{ textTransform: 'uppercase', fontWeight: 600, maxWidth: 220 }} />
          <button className="cs-btn" onClick={handleRdw} disabled={loadingRdw} data-testid="adm-rdw">
            {loadingRdw ? <Loader2 size={14} className="spin" /> : <Search size={14} />} {t('RDW ophalen', 'RDW ophalen')}
          </button>
          <ScanAutopapieren onResult={applyScan} testid="adm-scan" label={t('Scan papieren (AI)', 'Scan papieren (AI)')} />
        </div>
        <p style={{ fontSize: 12, color: 'var(--cs-text-muted)', marginTop: -8, marginBottom: 14 }}>
          {t('Geen kenteken? Upload een foto van de autopapieren (ook buitenlands, bv. Duits) — de AI vult merk, model, bouwjaar enz. in.', 'Geen kenteken? Upload een foto van de autopapieren (ook buitenlands, bv. Duits) — de AI vult merk, model, bouwjaar enz. in.')}
        </p>

        {/* Voertuigtype */}
        <label className="cs-section-title">{t('Voertuigtype', 'Voertuigtype')}</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }} data-testid="adm-voertuigtype">
          {VOERTUIG_TYPES.map(vt => (
            <button key={vt.v} type="button" onClick={() => upd('voertuigType', vt.v)} data-testid={`adm-vtype-${vt.v}`}
              className={`cs-btn ${d.voertuigType === vt.v ? 'primary' : ''}`} style={{ padding: '6px 11px', fontSize: 12.5 }}>
             <span style={{ marginRight: 4 }}>{vt.icon}</span>
{t(vt.label, vt.label)}
            </button>
          ))}
        </div>

        {/* Voertuiggegevens */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 10, marginBottom: 14 }}>
          <Field label={t('Merk', 'Merk')} testid="adm-merk" value={d.merk} onChange={v => upd('merk', v)} />
          <Field label={t('Model', 'Model')} testid="adm-model" value={d.model} onChange={v => upd('model', v)} />
          <Field label={t('Bouwjaar', 'Bouwjaar')} testid="adm-bouwjaar" value={d.bouwjaar} onChange={v => upd('bouwjaar', v)} />
          <Field label={t('Kilometerstand', 'Kilometerstand')} testid="adm-km" type="number" value={d.kmStand} onChange={v => upd('kmStand', v)} />
          <Field label={t('Inkoopprijs', 'Inkoopprijs')} testid="adm-inkoop" type="number" value={d.inkoopPrijs} onChange={v => upd('inkoopPrijs', v)} />
          <Field label={t('Vraagprijs', 'Vraagprijs')} testid="adm-vraagprijs" type="number" value={d.verkoopPrijs} onChange={v => upd('verkoopPrijs', v)} />
        </div>

        {/* Wat is er aan de auto gedaan */}
        <label className="cs-section-title">{t('Wat is er aan de auto gedaan?', 'Wat is er aan de auto gedaan?')}</label>
        <div style={{ marginBottom: 6 }}>
          {werk.map((w, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 130px 40px', gap: 6, marginBottom: 6 }} data-testid={`adm-werk-${i}`}>
              <input className="cs-input" placeholder={t('bv. Remblokken vervangen', 'bv. Remblokken vervangen')} value={w.omschrijving} onChange={e => setWerkRow(i, 'omschrijving', e.target.value)} data-testid={`adm-werk-oms-${i}`} />
              <input className="cs-input" type="number" placeholder={t('Kosten €', 'Kosten €')} value={w.kosten} onChange={e => setWerkRow(i, 'kosten', e.target.value)} data-testid={`adm-werk-kosten-${i}`} />
              <button onClick={() => delWerk(i)} data-testid={`adm-werk-del-${i}`}
                style={{ width: 36, height: 34, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0, border: '1px solid var(--cs-border)', borderRadius: 6, background: '#fff', color: '#dc2626', cursor: 'pointer' }}>
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
        <button className="cs-btn ghost" onClick={addWerk} data-testid="adm-werk-add" style={{ marginBottom: 14 }}><Plus size={14} /> {t('Regel toevoegen', 'Regel toevoegen')}</button>

        {/* Fase B — Pluspunten & onderhoud */}
        <label className="cs-section-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Wrench size={13} /> {t('Pluspunten & onderhoud', 'Pluspunten & onderhoud')}</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }} data-testid="adm-pluspunten">
          {[
            { k: 'distributie', label: t('Distributieriem vervangen', 'Distributieriem vervangen') },
            { k: 'apk', label: t('Nieuwe APK', 'Nieuwe APK') },
            { k: 'banden', label: t('Nieuwe banden', 'Nieuwe banden') },
            { k: 'remmen', label: t('Nieuwe remmen', 'Nieuwe remmen') },
          ].map(c => (
            <button key={c.k} type="button" onClick={() => updExtra(c.k, !extra[c.k])} data-testid={`adm-plus-${c.k}`}
              className={`cs-btn ${extra[c.k] ? 'primary' : ''}`} style={{ padding: '6px 12px', fontSize: 12.5 }}>
              {extra[c.k] ? '✓ ' : ''}{c.label}
            </button>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 10, marginBottom: 10 }}>
          <Field label={t('Aantal sleutels', 'Aantal sleutels')} testid="adm-sleutels" type="number" value={extra.sleutels} onChange={v => updExtra('sleutels', v)} />
          <div>
            <label style={{ fontSize: 12, color: 'var(--cs-text-muted)', fontWeight: 600, display: 'block', marginBottom: 3 }}>{t('Onderhoudshistorie', 'Onderhoudshistorie')}</label>
            <input className="cs-input" value={extra.onderhoudshistorie} onChange={e => updExtra('onderhoudshistorie', e.target.value)} data-testid="adm-onderhoud"
              placeholder={t('bv. volledig onderhoudsboekje, dealer onderhouden', 'bv. volledig onderhoudsboekje, dealer onderhouden')} />
          </div>
        </div>
        <label className="cs-section-title">{t('Schadepunten / aandachtspunten', 'Schadepunten / aandachtspunten')}</label>
        <input className="cs-input" value={extra.schadepunten} onChange={e => updExtra('schadepunten', e.target.value)} data-testid="adm-schade"
          placeholder={t('bv. kleine kras achterbumper, steenslag motorkap', 'bv. kleine kras achterbumper, steenslag motorkap')} style={{ marginBottom: 14 }} />

        {/* Fase C — Foto's vooraf */}
        <label className="cs-section-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Camera size={13} /> {t('Foto\'s (max 6) — AI herkent opties & schade', 'Foto\'s (max 6) — AI herkent opties & schade')}</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }} data-testid="adm-fotos">
          {fotos.map((f, i) => (
            <div key={i} style={{ position: 'relative' }} data-testid={`adm-foto-${i}`}>
              <img src={f} alt="" style={{ width: 90, height: 68, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--cs-border)' }} />
              <button onClick={() => delFoto(i)} data-testid={`adm-foto-del-${i}`} style={{ position: 'absolute', top: -7, right: -7, width: 22, height: 22, borderRadius: '50%', background: '#dc2626', color: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Trash2 size={12} /></button>
            </div>
          ))}
          {fotos.length < MAX_FOTOS && (
            <button type="button" onClick={() => fotoRef.current?.click()} data-testid="adm-foto-upload"
              style={{ width: 90, height: 68, border: '2px dashed var(--cs-border)', borderRadius: 8, background: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, color: 'var(--cs-text-muted)' }}>
              <Upload size={16} /><span style={{ fontSize: 10.5 }}>{t('Upload', 'Upload')}</span>
            </button>
          )}
          <input ref={fotoRef} type="file" accept="image/*" multiple hidden onChange={onFotos} data-testid="adm-foto-input" />
        </div>

        <label className="cs-section-title">{t('Extra info (optioneel)', 'Extra info (optioneel)')}</label>
        <textarea className="cs-input" rows={3} value={d.notitie} onChange={e => upd('notitie', e.target.value)} data-testid="adm-notitie"
          placeholder={t('bv. 2e eigenaar, rookvrij, nieuwe winterbanden…', 'bv. 2e eigenaar, rookvrij, nieuwe winterbanden…')} style={{ resize: 'vertical', marginBottom: 16 }} />

        <button className="cs-btn primary" onClick={maakAdvertentie} data-testid="adm-genereer" style={{ width: '100%', justifyContent: 'center', padding: 12 }}>
          <Sparkles size={16} /> {t('Maak advertentie', 'Maak advertentie')}
        </button>
        <p style={{ fontSize: 12, color: 'var(--cs-text-muted)', marginTop: 8, textAlign: 'center' }}>
          {fotos.length > 0
            ? t('AI analyseert je foto\'s direct mee voor opties, schade en waardebepaling.', 'AI analyseert je foto\'s direct mee voor opties, schade en waardebepaling.')
            : t('Tip: voeg foto\'s toe zodat de AI opties en schade automatisch herkent.', 'Tip: voeg foto\'s toe zodat de AI opties en schade automatisch herkent.')}
        </p>
      </div>

      {modalAuto && <VerkoopAdvertentie auto={modalAuto} onClose={() => setModalAuto(null)} />}
      <style>{`.spin{animation:spin 1s linear infinite}@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

const Field = ({ label, value, onChange, testid, type = 'text' }) => (
  <div>
    <label style={{ fontSize: 12, color: 'var(--cs-text-muted)', fontWeight: 600, display: 'block', marginBottom: 3 }}>{label}</label>
    <input className="cs-input" type={type} value={value} onChange={e => onChange(e.target.value)} data-testid={testid} />
  </div>
);
